import { AfterViewInit, Component, ElementRef, HostListener, ViewChild, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { RouterModule } from '@angular/router';

@Component({
  selector: 'app-website-usingai',
  imports: [CommonModule, RouterModule],
  standalone: true,
  templateUrl: './website-usingai.component.html',
  styleUrl: './website-usingai.component.scss'
})
export class WebsiteUsingaiComponent implements AfterViewInit, OnInit, OnDestroy {
  constructor(private renderer: Renderer2,
    @Inject(DOCUMENT) private _document: Document
  ) { }
  private jsonLdScript: any;
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const fullGraphSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/build-website-ai-tools#webpage",
          "url": "https://www.kalpitatechnologies.com/tech-blogs/build-website-ai-tools",
          "name": "4 Simple Steps to Build a Website Using AI Tools | Kalpita Technologies",
          "description": "Complete step-by-step guide to building a professional website using AI tools like Wix ADI, Framer AI, 10Web, Durable, and Hostinger — no coding required.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en",
          "datePublished": "2025-04-08"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/build-website-ai-tools#breadcrumb",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com" },
            { "@type": "ListItem", "position": 2, "name": "Blog Index", "item": "https://www.kalpitatechnologies.com/tech-blogs/blog" },
            { "@type": "ListItem", "position": 3, "name": "Build Website with AI", "item": "https://www.kalpitatechnologies.com/tech-blogs/build-website-ai-tools" }
          ]
        },
        {
          "@type": "BlogPosting",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/build-website-ai-tools#article",
          "headline": "4 Simple Steps to Build a Website Using AI Tools",
          "articleBody": "Gone are the days when building a professional website required technical skills... Step 1: Choose the Right AI Website Builder. Step 2: Generate Your Website Using AI. Step 3: Customize Your Website. Step 4: Publish...",
          "author": { "@type": "Person", "name": "Arjun Shetty" },
          "publisher": {
            "@type": "Organization",
            "name": "Kalpita Technologies",
            "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
          },
          "datePublished": "2025-04-08",
          "image": "https://www.kalpitatechnologies.com/assets/resources/blog/buildwebsite-usingai/websitebuild-usingAi-banner.webp"
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/build-website-ai-tools#howto",
          "name": "How to Build a Website Using AI Tools in 4 Simple Steps",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Choose AI Builder", "text": "Select an AI website builder like Wix ADI, Framer, or 10Web." },
            { "@type": "HowToStep", "position": 2, "name": "Generate Site", "text": "Answer AI questions to auto-generate layouts and content." },
            { "@type": "HowToStep", "position": 3, "name": "Customize", "text": "Edit text with AI writers and enhance design with Canva AI." },
            { "@type": "HowToStep", "position": 4, "name": "Publish", "text": "Choose a domain and go live instantly." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/tech-blogs/build-website-ai-tools#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Can I really build a website without coding using AI tools?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes. AI website builders are designed for non-technical users to generate layouts and design automatically."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(fullGraphSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  @ViewChild('tableresponsiveImage', { static: false }) image!: ElementRef<HTMLImageElement>;

  originalWidth = 1920;
  originalHeight = 476;

  ngAfterViewInit(): void {
    this.resizeImageMap();
    this.image.nativeElement.onload = () => this.resizeImageMap();
  }

  @HostListener('window:resize')
  resizeImageMap(): void {
    const img = this.image.nativeElement;
    if (!img) return;

    const wRatio = img.clientWidth / this.originalWidth;
    const hRatio = img.clientHeight / this.originalHeight;

    document.querySelectorAll<HTMLAreaElement>('map[name="table-image-map"] area')
      .forEach(area => {
        const rawCoords =
          area.getAttribute('data-original-coords')
          || area.getAttribute('coords')
          || '';
        if (!rawCoords) return;

        area.setAttribute('data-original-coords', rawCoords);
        const scaled = rawCoords
          .split(',')
          .map((c, i) =>
            Math.round(
              Number(c) * (i % 2 === 0 ? wRatio : hRatio)
            )
          )
          .join(',');

        area.setAttribute('coords', scaled);
      });
  }

  scrollToSection(sectionId: string): void {
    const el = document.getElementById(sectionId);
    if (!el) return;
    const yOffset = 5 * 16;
    const top =
      el.getBoundingClientRect().top + window.pageYOffset - yOffset;
    window.scrollTo({ top, behavior: 'smooth' });
  }
}
